import { Button, Toast } from 'components';
import { useCallback, useMemo, useState } from 'react';
import { ProtectedLayout } from 'hoc';
import useCheckMicAccess from 'hooks/useCheckMicAccess';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';
import { usePaginatedResults } from 'hooks/usePaginatedResults';
import { useMe } from 'hooks/auth';
import {
  AnamnesisDialog,
  HomeBottomNavbar,
  HomeFilterHeader,
  PausedConsultations,
  ResultCard,
} from 'widgets';
import { useQueryClient } from '@tanstack/react-query';
import { Container, Empty, List, Page, Section, Sentinel } from './home.styles';
import { useHomeFilters, usePatientSync } from 'providers';
import { useAutoUpdate } from 'hooks/useAutoUpdate';
import type { ExportStatus } from 'types/result';
import { getEndDate, getStartDate } from './utils';

export default function Home() {
  const { t } = useTranslation();
  const { micPermissionDenied, dismiss } = useCheckMicAccess();
  const navigate = useNavigate();
  const [isPatientDialogOpen, setPatientDialogOpen] = useState(false);
  const [patientIdInput, setPatientIdInput] = useState('');
  const [patientIdError, setPatientIdError] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { data: user } = useMe();
  const { filters } = useHomeFilters();
  const { reportManualSwitch, syncState, switchToPendingPatient } = usePatientSync();

  const fetchParams = useMemo(() => {
    return {
      search: filters.searchQuery,
      start_date: getStartDate(filters.dateRange),
      end_date: getEndDate(filters.dateRange),
      ordering: filters.ordering,
      export_status: filters.exportStatusFilter,
    };
  }, [filters]);

  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    resultsState,
    handleStatusUpdate: updateResultStatus,
    sentinelRef,
    fetchNextPage,
  } = usePaginatedResults(fetchParams);

  const handleStatusUpdate = useCallback(
    (updatedStatus: ExportStatus, resultId: number) => {
      updateResultStatus(updatedStatus, resultId);
      void queryClient.invalidateQueries({ queryKey: ['results', fetchParams] });
    },
    [updateResultStatus, queryClient, fetchParams]
  );

  useAutoUpdate();

  const resetPatientDialogState = useCallback(() => {
    setPatientIdInput('');
    setPatientIdError(null);
  }, []);

  const openPatientDialog = useCallback(() => {
    resetPatientDialogState();
    setPatientDialogOpen(true);
  }, [resetPatientDialogState]);

  const handlePatientDialogOpenChange = useCallback(
    (open: boolean) => {
      setPatientDialogOpen(open);
      if (!open) {
        resetPatientDialogState();
      }
    },
    [resetPatientDialogState]
  );

  // Note: handled via handlePatientIdValueChange for the dialog widget
  const handlePatientIdValueChange = useCallback(
    (value: string) => {
      setPatientIdInput(value);
      if (patientIdError) {
        setPatientIdError(null);
      }
    },
    [patientIdError]
  );

  const navigateToRecording = useCallback(
    (medatixxPatientId?: string) => {
      void navigate(
        medatixxPatientId
          ? `/patient-overview/${encodeURIComponent(medatixxPatientId)}`
          : '/recording'
      );
    },
    [navigate]
  );

  const startManualRecording = useCallback(
    (patientData?: { medatixxId: string } | null) => {
      const trimmedPatientId = patientIdInput.trim();
      if (!trimmedPatientId && !patientData) {
        setPatientIdError(t('home.anamnesisLookupDialog.errorRequired'));
        return;
      }

      let resolvedMedatixxId: string | undefined;
      if (patientData) {
        resolvedMedatixxId = patientData.medatixxId || trimmedPatientId;
      } else {
        resolvedMedatixxId = trimmedPatientId;
      }
      handlePatientDialogOpenChange(false);
      reportManualSwitch(resolvedMedatixxId);
      navigateToRecording(resolvedMedatixxId);
    },
    [patientIdInput, t, handlePatientDialogOpenChange, reportManualSwitch, navigateToRecording]
  );

  const openCurrentPatient = useCallback(() => {
    if (syncState === 'out-of-sync') {
      switchToPendingPatient();
      return;
    }
    openPatientDialog();
  }, [syncState, openPatientDialog, switchToPendingPatient]);

  const greetingTitle = user?.firstName
    ? t('home.greeting', { firstName: user.firstName })
    : undefined;

  return (
    <ProtectedLayout title={greetingTitle} hasBackButton={false} showsPatientMismatchBanner={false}>
      <Toast message={t('mic_access_denied')} visible={micPermissionDenied} onClose={dismiss} />
      <AnamnesisDialog
        open={isPatientDialogOpen}
        onOpenChange={handlePatientDialogOpenChange}
        value={patientIdInput}
        error={patientIdError}
        onChange={handlePatientIdValueChange}
        onSubmit={startManualRecording}
      />
      <Page>
        <Container>
          <PausedConsultations />
          <Section>
            <HomeFilterHeader data={data?.pages[0]} />
            <List role="list" aria-label={t('home.consultationList')}>
              {isLoading ? (
                <Empty>{t('home.loadingConversations')}</Empty>
              ) : resultsState.length ? (
                <>
                  {resultsState.map((result, index) => (
                    <ResultCard
                      key={result.id}
                      result={result}
                      resultIndex={index}
                      handleStatusUpdate={handleStatusUpdate}
                    />
                  ))}
                  {hasNextPage && (
                    <Sentinel
                      ref={sentinelRef}
                      aria-hidden="true"
                      data-pagination-sentinel="home"
                    />
                  )}
                  {isFetchingNextPage && <Empty>{t('home.loadingConversations')}</Empty>}
                </>
              ) : isError ? (
                <Empty role="alert">{t('home.loadError')}</Empty>
              ) : (
                <Empty>
                  {data === undefined ? t('home.loadingConversations') : t('home.noConversations')}
                </Empty>
              )}
            </List>
            {isError && resultsState.length > 0 && (
              <Empty role="alert">
                {t('home.paginationError')}{' '}
                <Button
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => void fetchNextPage()}
                >
                  {t('general.retry')}
                </Button>
              </Empty>
            )}
          </Section>
        </Container>
        <HomeBottomNavbar onRecordButtonClick={openCurrentPatient} />
      </Page>
    </ProtectedLayout>
  );
}
